import { useState } from "react";
import { router } from "@inertiajs/react";
import { Layout } from "@/components/layout";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, ArrowLeft, Upload as UploadIcon } from "lucide-react";
import { ImageUploadStep } from "@/pages/Submissions/components/ImageUploadStep";

interface Props {
  user: { email: string };
}

export default function Upload({ user }: Props) {
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [croppedFile, setCroppedFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClearImage = () => {
    setImageFile(null);
    setCroppedFile(null);
  };

  const handleSubmit = () => {
    if (!imageFile) {
      setError("Envie uma imagem antes de continuar");
      return;
    }
    if (!croppedFile) {
      setError("Recorte a imagem antes de continuar");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    router.post(
      "/photos",
      { photo: { image: croppedFile } },
      {
        onError: (errors) => {
          const errorMessages = Object.values(errors).flat().join(", ");
          setError(errorMessages);
          setIsSubmitting(false);
        },
      },
    );
  };

  return (
    <Layout title="Nova Foto" user={user}>
      <div className="mx-auto max-w-4xl px-4 py-10">
        <Card className="border-border bg-card backdrop-blur">
          <CardHeader>
            <CardTitle className="text-xl">Enviar Foto</CardTitle>
            <CardDescription>
              Escolha uma foto e ajuste o recorte
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {error && (
              <Alert variant="error">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <ImageUploadStep
              imageFile={imageFile}
              onFileSelect={(file) => {
                setImageFile(file);
                setCroppedFile(null);
              }}
              onCropReady={setCroppedFile}
              onClearImage={handleClearImage}
            />

            <div className="flex items-center justify-between border-t border-border pt-6">
              <Button
                variant="ghost"
                onClick={() => router.visit("/photos")}
                className="gap-2 text-muted-foreground hover:text-foreground"
              >
                <ArrowLeft size={16} />
                Voltar
              </Button>

              <Button
                onClick={handleSubmit}
                disabled={isSubmitting || !imageFile}
                className="gap-2"
              >
                {isSubmitting ? "Enviando..." : "Enviar Foto"}
                <UploadIcon size={16} />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
